import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../utils/i18n';
import './ApiDocumentationPanel.css';
import * as XLSX from 'xlsx';
import envService from '../../services/envService';
import LoadingSpinner from '../LoadingSpinner';
import FailModal from '../FailModal';
import ErrorModal from '../ErrorModal';
import PDFViewer from '../PDFViewer';
const API_FILE: string = envService.getApiFileUrl();

interface ApiDocumentationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    title: string;
    data: string;
    detail: string;
    requestDataApproveReason: string;
    requestId: string;
  };
}

interface ApiDetailData {
  request_data_token: string;
  user_register_detail: {
    user_register_detail_api_key: string;
  };
  data_open: {
    data_open_url_api: string;
    url_api_on_service: string;
    data_open_name_th: string;
    data_open_name_en: string;
  };
  data_open_title: {
    data_open_title_name_th: string;
    data_open_title_name_en: string;
  };
  parameter_data_list: Array<{
    parameter_open_name: string;
    parameter_open_type: string;
  }>;
  data_open_file_attachments: Array<{
    fileAttachmentId: string;
    referenceFileId: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    fileExtName: string;
  }>;
}

const ApiDocumentationPanel: React.FC<ApiDocumentationPanelProps> = ({ isOpen, onClose, data }) => {
  const { t, currentLocale } = useTranslation();
  const [apiData, setApiData] = useState<ApiDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [parameterValues, setParameterValues] = useState<{ [key: string]: string }>({});
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isDownloadingJSON, setIsDownloadingJSON] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isApiLogLoading, setIsApiLogLoading] = useState(false);
  const [apiLogStatus, setApiLogStatus] = useState<string | null>(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState('');
  const [currentPdfFileName, setCurrentPdfFileName] = useState('');

  useEffect(() => {
    if (isOpen && data.requestId) {
      fetchApiDetails();
    }
  }, [isOpen, data.requestId]);

  const fetchApiDetails = async () => {
    try {
      setIsLoading(true);
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const response = await fetch(`${API_BASE_URL}/api/request-data-detail/${data.requestId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setApiData(result);
    } catch (error) {
      console.error('Error fetching API details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopyNotification(true);
    setTimeout(() => {
      setShowCopyNotification(false);
    }, 2000);
  };

  const handleFileDownload = async (referenceFileId: string, fileName: string) => {
    try {
      setDownloadingFileId(referenceFileId);

      // Simulate a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));

      const downloadUrl = API_FILE + `/downloadFile?fileId=${referenceFileId}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(t('apiDocPanel.errors.downloadFileError') || 'เกิดข้อผิดพลาดในการดาวน์โหลดไฟล์');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handlePdfView = (referenceFileId: string, fileName: string) => {
    const pdfUrl = API_FILE + `/downloadFile?fileId=${referenceFileId}`;
    setCurrentPdfUrl(pdfUrl);
    setCurrentPdfFileName(fileName);
    setShowPdfViewer(true);
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    setCurrentPdfUrl('');
    setCurrentPdfFileName('');
  };

  const getFileIcon = (fileExtName: string): string => {
    const ext = fileExtName.toLowerCase();
    if (ext === 'pdf') return '📄';
    if (ext === 'doc' || ext === 'docx') return '📝';
    if (ext === 'xls' || ext === 'xlsx') return '📊';
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png') return '🖼️';
    return '📁';
  };

  const handleParameterChange = (paramName: string, value: string) => {
    setParameterValues(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Build URL with parameters
  const buildApiUrl = (): string => {
    if (!apiData?.data_open?.url_api_on_service) return '';

    const baseUrl = apiData.data_open.url_api_on_service;
    const params = new URLSearchParams();

    // Add parameter values to URL with UTF-8 encoding
    Object.entries(parameterValues).forEach(([key, value]) => {
      if (value.trim()) {
        // Ensure UTF-8 encoding for parameter values

        let enText = encodeURIComponent(value)
        params.append(key, enText);
        // console.log("value", value)
        // console.log("enText", enText)
        // console.log("de value", decodeURIComponent(encodeURIComponent(value)))
      }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  // Update API call counters
  const updateApiCounters = async (success: boolean) => {
    try {
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const counterData = {
        requestDataSum: 1,
        requestDataPass: success ? 1 : 0,
        requestDataFail: success ? 0 : 1,
        updatedBy: "SYSTEM"
      };

      await fetch(`${API_BASE_URL}/api/request-data/${data.requestId}/counters`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(counterData)
      });
    } catch (error) {
      console.error('Error updating API counters:', error);
      // Don't show error to user as this is background tracking
    }
  };

  // Send API Log function
  const sendApiLog = async (status: string) => {
    // setIsApiLogLoading(true);
    // setApiLogStatus(null);


    try {
      const now = new Date();
      const sendTime = now.toISOString().slice(0, 19).replace('T', ' ');
      const sendDate = now.toISOString().slice(0, 10);

      const apiLogData = {
        requestDataId: data.requestId,
        statusLog: status, // Will be set to 1 for success, 0 for failure
        sendTime: sendTime,
        sendDate: sendDate,
        createdBy: "SYSTEM",
        updatedBy: "SYSTEM"
      };
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';

      const response = await fetch(`${API_BASE_URL}/api/api-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiLogData)
      });

      if (response.ok) {
        console.log('API log sent successfully');
      }
    } catch (error) {
      console.error('Network error:', error);
    } finally {
    }
  };

  // Fetch data from API with proper headers
  const fetchApiData = async () => {
    if (!apiData?.user_register_detail?.user_register_detail_api_key || !apiData?.request_data_token) {
      setFailMessage(t('apiDocPanel.errors.invalidApiKeyOrToken'));
      setShowFailModal(true);
      return;
    }

    try {
      setIsApiLoading(true);
      const url = buildApiUrl();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'TOKEN-KEY': `${apiData.request_data_token}`,
          'API-KEY': apiData.user_register_detail.user_register_detail_api_key,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setFetchedData(result);
      setShowCopyNotification(true);
      setTimeout(() => {
        setShowCopyNotification(false);
      }, 2000);

      // Update counters for successful API call
      // await updateApiCounters(true);
      // sendApiLog("1")

    } catch (error) {
      console.error('Error fetching API data:', error);
      setShowErrorModal(true);

      // Update counters for failed API call
      // await updateApiCounters(false);
      // sendApiLog("0")
    } finally {
      setIsApiLoading(false);
    }
  };

  // Handle fail modal close
  const handleFailModalClose = () => {
    setShowFailModal(false);
    setFailMessage('');
  };

  // Handle error modal close
  const handleErrorModalClose = () => {
    setShowErrorModal(false);
  };

  // Helper function to detect and flatten nested data structures
  const processHierarchicalData = (data: any): { hasNested: boolean; flattened: any[]; sheets: { [key: string]: any[] } } => {
    const dataArray = Array.isArray(data) ? data : [data];
    const sheets: { [key: string]: any[] } = {};
    const flattened: any[] = [];
    let hasNested = false;

    // Check if this is a response wrapper (has data array inside)
    let actualData = dataArray;
    if (dataArray.length === 1 && dataArray[0].data && Array.isArray(dataArray[0].data)) {
      actualData = dataArray[0].data;
    }

    actualData.forEach((item, index) => {
      // Check for any array properties that could be nested data
      const nestedArrays = Object.keys(item).filter(key =>
        Array.isArray(item[key]) && item[key].length > 0 && typeof item[key][0] === 'object'
      );

      if (nestedArrays.length > 0) {
        hasNested = true;

        // Create flattened version for CSV
        nestedArrays.forEach(arrayKey => {
          item[arrayKey].forEach((nestedItem: any, nestedIndex: number) => {
            const flatItem = { ...item };
            delete flatItem[arrayKey]; // Remove the nested array

            // Add row identifier for nested items
            flatItem['parent_row_index'] = index + 1;
            flatItem['nested_item_index'] = nestedIndex + 1;

            // Add nested item properties with prefix to avoid conflicts
            Object.keys(nestedItem).forEach(nestedKey => {
              flatItem[`${arrayKey}_${nestedKey}`] = nestedItem[nestedKey];
            });

            flattened.push(flatItem);
          });
        });

        // Create separate sheets for Excel
        nestedArrays.forEach(arrayKey => {
          const sheetName = `${arrayKey}_${index + 1}`;
          if (!sheets[sheetName]) {
            sheets[sheetName] = [];
          }
          sheets[sheetName] = [...sheets[sheetName], ...item[arrayKey]];
        });
      } else {
        // For items without nested data, still add them to flattened
        const flatItem = { ...item };
        flatItem['parent_row_index'] = index + 1;
        flatItem['nested_item_index'] = 0; // 0 indicates no nested data
        flattened.push(flatItem);
      }
    });

    return { hasNested, flattened, sheets };
  };

  // Download functions
  const downloadExcel = async () => {
    if (!fetchedData) {
      alert(t('apiDocPanel.errors.fetchDataFirst'));
      return;
    }

    try {
      setIsDownloadingExcel(true);

      // Simulate processing time to show loading state
      await new Promise(resolve => setTimeout(resolve, 800));

      const workbook = XLSX.utils.book_new();
      const { hasNested, flattened, sheets } = processHierarchicalData(fetchedData);

      if (hasNested) {
        // Create main sheet with parent data (without nested arrays)
        const mainData = Array.isArray(fetchedData) ? fetchedData : [fetchedData];
        const cleanMainData = mainData.map(item => {
          const cleanItem = { ...item };
          // Remove array properties for main sheet
          Object.keys(cleanItem).forEach(key => {
            if (Array.isArray(cleanItem[key])) {
              delete cleanItem[key];
            }
          });
          return cleanItem;
        });

        const mainWorksheet = XLSX.utils.json_to_sheet(cleanMainData);
        XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Main Data');

        // Create separate sheets for nested data
        Object.keys(sheets).forEach(sheetName => {
          if (sheets[sheetName].length > 0) {
            // Limit sheet name to 31 characters (Excel limitation)
            const limitedSheetName = sheetName.substring(0, 31);
            const nestedWorksheet = XLSX.utils.json_to_sheet(sheets[sheetName]);
            XLSX.utils.book_append_sheet(workbook, nestedWorksheet, limitedSheetName);
          }
        });

        // Also create a flattened sheet for complete data view
        if (flattened.length > 0) {
          const flatWorksheet = XLSX.utils.json_to_sheet(flattened);
          XLSX.utils.book_append_sheet(workbook, flatWorksheet, 'Flattened Data');
        }
      } else {
        // Simple data structure - single sheet
        const dataArray = Array.isArray(fetchedData) ? fetchedData : [fetchedData];
        const worksheet = XLSX.utils.json_to_sheet(dataArray);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      }

      const fileName = `api_data_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert(t('apiDocPanel.errors.downloadExcelError'));
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const downloadCSV = async () => {
    if (!fetchedData) {
      alert(t('apiDocPanel.errors.fetchDataFirst'));
      return;
    }

    try {
      setIsDownloadingCSV(true);

      // Simulate processing time to show loading state
      await new Promise(resolve => setTimeout(resolve, 600));

      const { hasNested, flattened } = processHierarchicalData(fetchedData);

      let csvData;
      if (hasNested && flattened.length > 0) {
        // Use flattened data for hierarchical structures
        csvData = flattened;
      } else {
        // Use original data for simple structures
        csvData = Array.isArray(fetchedData) ? fetchedData : [fetchedData];
      }

      const worksheet = XLSX.utils.json_to_sheet(csvData);
      const csv = XLSX.utils.sheet_to_csv(worksheet);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `api_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert(t('apiDocPanel.errors.downloadCSVError'));
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const downloadJSON = async () => {
    if (!fetchedData) {
      alert(t('apiDocPanel.errors.fetchDataFirst'));
      return;
    }

    try {
      setIsDownloadingJSON(true);

      // Simulate processing time to show loading state
      await new Promise(resolve => setTimeout(resolve, 400));

      const jsonString = JSON.stringify(fetchedData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `api_data_${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading JSON:', error);
      alert(t('apiDocPanel.errors.downloadJSONError'));
    } finally {
      setIsDownloadingJSON(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="api-documentation-panel">
      {/* Copy Notification */}
      {showCopyNotification && (
        <div className="copy-notification">
          <div className="copy-notification-content">
            {fetchedData ? t('apiDocPanel.notifications.fetchSuccess') : t('apiDocPanel.notifications.copySuccess')}
          </div>
        </div>
      )}

      {/* Header with Back Button */}
      <div className="panel-header">
        <button onClick={onClose} className="back-button">
          ‹
        </button>
        <div>
          <h1 className="panel-title">
            {apiData?.data_open_title && (currentLocale === 'th-TH' ? apiData.data_open_title.data_open_title_name_th : apiData.data_open_title.data_open_title_name_en) || '...'}
          </h1>
          <p className="panel-subtitle">
            {apiData?.data_open && (currentLocale === 'th-TH' ? apiData.data_open.data_open_name_th : apiData.data_open.data_open_name_en) || '...'}
          </p>
        </div>
      </div>

      {/* API Usage Section */}
      <div className="api-usage-section">
        <h3 className="section-title">
          {t('apiDocPanel.usageGuide')}
        </h3>

        {/* User Guide Files List */}
        <div className="user-guide-files">
          {apiData?.data_open_file_attachments && apiData.data_open_file_attachments.length > 0 ? (
            apiData.data_open_file_attachments.map((file, index) => (
              <div key={index} className="file-item">
                <div
                  className="file-info"
                  onClick={() => handleFileDownload(file.referenceFileId, file.fileName)}
                >
                  <span className="file-icon">
                    {downloadingFileId === file.referenceFileId ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      getFileIcon(file.fileExtName)
                    )}
                  </span>
                  <div>
                    <span className="file-name">
                      {file.fileName}
                    </span>
                    <div className="file-size">
                      {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <div className="file-actions">
                  {file.fileExtName.toLowerCase() === 'pdf' && (
                    <button
                      className="view-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePdfView(file.referenceFileId, file.fileName);
                      }}
                      title={t('pdfViewer.viewPDF')}
                    >
                      🔍
                    </button>
                  )}
                  {/* <button
                    className="download-button-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileDownload(file.referenceFileId, file.fileName);
                    }}
                    title="Download"
                  >
                    ⬇
                  </button> */}
                </div>
              </div>
            ))
          ) : (
            <div className="no-files-message">
              {isLoading ? "..." : t('apiDocPanel.noGuideFiles')}
            </div>
          )}
        </div>
      </div>

      {/* API Testing Section */}
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-text">{t('apiDocPanel.loading')}</div>
        </div>
      ) : (
        <div className="api-testing-section">
          <div className="credentials-row">
            {/* APIKEY Field */}
            <div className="credential-field">
              <label className="field-label">
                APIKEY <span className="required-asterisk">*</span>
              </label>
              <div className="input-container">
                <input
                  type="password"
                  value={apiData?.user_register_detail?.user_register_detail_api_key || ''}
                  readOnly
                  className="credential-input no-copy-input"

                />
                <button
                  onClick={() => copyToClipboard(apiData?.user_register_detail?.user_register_detail_api_key || '')}
                  className="copy-button"
                >
                  📋
                </button>
              </div>
            </div>

            {/* TOKEN Field */}
            <div className="credential-field">
              <label className="field-label">
                TOKEN <span className="required-asterisk">*</span>
              </label>
              <div className="input-container">
                <input
                  type="password"
                  value={apiData?.request_data_token || ''}
                  readOnly
                  className="credential-input no-copy-input"
                />
                <button
                  onClick={() => copyToClipboard(apiData?.request_data_token || '')}
                  className="copy-button"
                >
                  📋
                </button>
              </div>
            </div>
          </div>

          {/* GET URL Field */}
          <div className="url-field">
            <label className="field-label">
              GET <span className="required-asterisk">*</span>
            </label>
            <div className="url-input-row">
              <div className="input-container"
                style={{ width: "calc(100% - 0px)" }}
              >
                <input
                  type="text"
                  value={buildApiUrl()}
                  readOnly
                  className="credential-input no-copy-input"
                />
                <button
                  onClick={() => copyToClipboard(buildApiUrl())}
                  className="copy-button"
                >
                  📋
                </button>
              </div>
              <button
                style={{
                  // width: "115px"
                }}
                className="send-button"
                onClick={fetchApiData}
                disabled={isApiLoading}
              >
                {/* {isApiLoading ? t('apiDocPanel.sending') : t('apiDocPanel.send')}
                 */}
                {isApiLoading ? <LoadingSpinner size="small" /> : t('apiDocPanel.send')}


              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Log Section */}
      {/*<div className="api-log-section" style={{ marginTop: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
        <h4 className="subsection-title">{t('apiDocPanel.sendApiLog')}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button 
            onClick={sendApiLog}
            disabled={isApiLogLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: isApiLogLoading ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: isApiLogLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            {isApiLogLoading && <LoadingSpinner size="small" />}
            {isApiLogLoading ? t('apiDocPanel.sending') : t('apiDocPanel.sendApiLog')}
          </button>
          
          {apiLogStatus && (
            <span style={{
              padding: '5px 10px',
              borderRadius: '3px',
              fontSize: '14px',
              backgroundColor: apiLogStatus === 'success' ? '#d4edda' : '#f8d7da',
              color: apiLogStatus === 'success' ? '#155724' : '#721c24',
              border: `1px solid ${apiLogStatus === 'success' ? '#c3e6cb' : '#f5c6cb'}`
            }}>
              {apiLogStatus === 'success' && t('apiDocPanel.apiLogSentSuccess')}
              {apiLogStatus === 'failure' && t('apiDocPanel.apiRequestFailed')}
              {apiLogStatus === 'error' && t('apiDocPanel.networkErrorOccurred')}
            </span>
          )}
        </div>
      </div>
      */}

      {/* Download Section */}
      <div className="download-section">
        <div className="download-buttons">
          <button
            className="download-button"
            onClick={downloadExcel}
            disabled={!fetchedData || isDownloadingExcel}
          >
            {isDownloadingExcel ? (
              <>
                <LoadingSpinner size="small" />
                <span ></span>
              </>
            ) : (
              t('apiDocPanel.downloadExcel')
            )}
          </button>
          <button
            className="download-button"
            onClick={downloadCSV}
            disabled={!fetchedData || isDownloadingCSV}
          >
            {isDownloadingCSV ? (
              <>
                <LoadingSpinner size="small" />
                <span ></span>
              </>
            ) : (
              t('apiDocPanel.downloadCSV')
            )}
          </button>
          <button
            className="download-button"
            onClick={downloadJSON}
            disabled={!fetchedData || isDownloadingJSON}
          >
            {isDownloadingJSON ? (
              <>
                <LoadingSpinner size="small" />
                <span ></span>
              </>
            ) : (
              t('apiDocPanel.downloadJSON')
            )}
          </button>
        </div>
      </div>

      {/* API Response Display
      {fetchedData && (
        <div className="api-response-section">
          <h4 className="subsection-title">
            {t('apiDocPanel.apiResult')}
          </h4>
          <div className="api-response-container">
            <pre className="api-response-content">
              {JSON.stringify(fetchedData, null, 2)}
            </pre>
          </div>
        </div>
      )} */}

      {/* Parameters Table */}
      <div>
        <h4 className="subsection-title">
          {t('apiDocPanel.parametersList')}
        </h4>
        <table className="parameters-table">
          <thead>
            <tr className="table-header">
              <th className="table-header-cell">
                {t('apiDocPanel.parameterName')}
              </th>
              <th className="table-header-cell">
                {t('apiDocPanel.parameterValue')}
              </th>
            </tr>
          </thead>
          <tbody>
            {apiData?.parameter_data_list && apiData.parameter_data_list.length > 0 ? (
              apiData.parameter_data_list.map((param, index) => (
                <tr key={index}>
                  <td className="table-cell">
                    {param.parameter_open_name}
                  </td>
                  <td className="table-input-cell">
                    <input
                      type={param.parameter_open_type === 'number' ? 'number' : 'text'}
                      value={parameterValues[param.parameter_open_name] || ''}
                      onChange={(e) => handleParameterChange(param.parameter_open_name, e.target.value)}
                      placeholder={t('apiDocPanel.parameterPlaceholder', { type: param.parameter_open_type })}
                      className="parameter-input"
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="no-parameters-cell">
                  {isLoading ? "..." : t('apiDocPanel.noParameters')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Rejection Reason if exists */}
      {data.requestDataApproveReason && (
        <div className="rejection-reason-section">
          <h4 className="subsection-title">
            {t('data.detailsPanel.rejectReasonLabel')}
          </h4>
          <div className="rejection-reason-box">
            {data.requestDataApproveReason}
          </div>
        </div>
      )}

      {/* Fail Modal */}
      <FailModal
        isOpen={showFailModal}
        onClose={handleFailModalClose}
        onConfirm={handleFailModalClose}
        message={failMessage}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={handleErrorModalClose}
      />

      {/* PDF Viewer */}
      {showPdfViewer && <PDFViewer
        isVisible={showPdfViewer}
        onClose={handleClosePdfViewer}
        fileUrl={currentPdfUrl}
        fileName={currentPdfFileName}
      />}


    </div>
  );
};

export default ApiDocumentationPanel;
